import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

export const ContactForm = ({ sourcePage = 'Allgemein', sourceTitle = 'Terminanfrage' }) => {
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    anliegen: '',
    wunschtermin: '',
    nachricht: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Form submitted:', {
      ...formData,
      source_page: sourcePage,
      source_title: sourceTitle,
      subject: `Terminanfrage – ${sourceTitle}`
    });
    
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 text-center shadow-lg border border-[#0b382a]/10"
      >
        <div className="w-16 h-16 bg-[#0b382a]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-[#0b382a]" />
        </div>
        <h3 className="text-xl font-semibold text-[#0b382a] mb-2">Vielen Dank für Ihre Anfrage!</h3>
        <p className="text-[#0b382a]/70">Wir werden uns schnellstmöglich bei Ihnen melden.</p>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg border border-[#0b382a]/10"
    >
      <h3 className="text-xl font-semibold text-[#0b382a] mb-6">Online-Terminanfrage</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-[#0b382a] mb-1.5">Vorname *</label>
          <input
            type="text"
            name="vorname"
            value={formData.vorname}
            onChange={handleChange}
            required
            className="form-input"
            placeholder="Ihr Vorname"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0b382a] mb-1.5">Nachname *</label>
          <input
            type="text"
            name="nachname"
            value={formData.nachname}
            onChange={handleChange}
            required
            className="form-input"
            placeholder="Ihr Nachname"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-[#0b382a] mb-1.5">E-Mail *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="form-input"
            placeholder="ihre@email.de"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0b382a] mb-1.5">Telefon</label>
          <input
            type="tel"
            name="telefon"
            value={formData.telefon}
            onChange={handleChange}
            className="form-input"
            placeholder="Ihre Telefonnummer"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-[#0b382a] mb-1.5">Anliegen *</label>
          <select
            name="anliegen"
            value={formData.anliegen}
            onChange={handleChange}
            required
            className="form-input"
          >
            <option value="">Bitte wählen...</option>
            <option value="hausarzt">Hausärztliche Betreuung</option>
            <option value="kardiologie">Kardiologie / Herz-Check</option>
            <option value="mrt">MRT-Untersuchung</option>
            <option value="vorsorge">Vorsorgeuntersuchung</option>
            <option value="akut">Akute Beschwerden</option>
            <option value="sonstiges">Sonstiges</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#0b382a] mb-1.5">Wunschtermin</label>
          <input
            type="date"
            name="wunschtermin"
            value={formData.wunschtermin}
            onChange={handleChange}
            className="form-input"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-[#0b382a] mb-1.5">Nachricht</label>
        <textarea
          name="nachricht"
          value={formData.nachricht}
          onChange={handleChange}
          rows={4}
          className="form-input resize-none"
          placeholder="Beschreiben Sie kurz Ihr Anliegen..."
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full btn-pill bg-[#0b382a] hover:bg-[#0d4533] text-white py-6"
      >
        {isSubmitting ? (
          <span className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Wird gesendet...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Terminanfrage senden
          </span>
        )}
      </Button>

      <p className="text-xs text-[#0b382a]/50 mt-4 text-center">
        Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu.
      </p>
    </motion.form>
  );
};

export default ContactForm;
